import { useState, useCallback, useEffect } from 'react';
import { TextEditingState, TextStyle, EditableElement } from './types';

export interface UseTextEditorOptions {
  onTextChange?: (id: string, content: string, style: TextStyle, newHeight?: number) => void;
  defaultStyle?: Partial<TextStyle>;
}

const defaultTextStyle: TextStyle = {
  fontSize: 16,
  fontFamily: 'Arial',
  fontStyle: 'normal',
  fill: '#000000',
  align: 'left',
  lineHeight: 1.2,
  letterSpacing: 0,
};

export const useTextEditor = (options: UseTextEditorOptions = {}) => {
  const [editingState, setEditingState] = useState<TextEditingState>({
    editingTextId: null,
    textContent: '',
    textStyle: { ...defaultTextStyle, ...options.defaultStyle },
    cursorPosition: { x: 0, y: 0 },
  });

  // Start editing a text element
  const startEditing = useCallback((element: EditableElement, initialStyle?: TextStyle) => {
    // Calculate cursor position based on element position
    const x = element.x;
    const y = element.y;

    // Use the provided initialStyle if available, otherwise fall back to element data or defaults
    const elementStyle: TextStyle = initialStyle || {
      fontSize: element.data?.fontSize || defaultTextStyle.fontSize,
      fontFamily: element.data?.fontFamily || defaultTextStyle.fontFamily,
      fontStyle: element.data?.fontStyle || defaultTextStyle.fontStyle,
      fill: element.data?.fill || element.data?.textFill || defaultTextStyle.fill,
      align: element.data?.align || defaultTextStyle.align,
      lineHeight: element.data?.lineHeight || defaultTextStyle.lineHeight,
      letterSpacing: element.data?.letterSpacing || defaultTextStyle.letterSpacing,
    };

    setEditingState({
      editingTextId: element.id,
      textContent: element.data?.text || '',
      textStyle: { ...elementStyle, ...options.defaultStyle },
      cursorPosition: { x, y },
    });
  }, [options.defaultStyle]);

  // Save the edited text
  const saveText = useCallback((content?: string, style?: TextStyle, newHeight?: number) => {
    if (editingState.editingTextId && options.onTextChange) {
      options.onTextChange(
        editingState.editingTextId,
        content !== undefined ? content : editingState.textContent,
        style !== undefined ? style : editingState.textStyle,
        newHeight
      );
    }
    setEditingState({
      editingTextId: null,
      textContent: '',
      textStyle: { ...defaultTextStyle, ...options.defaultStyle },
      cursorPosition: { x: 0, y: 0 },
    });
  }, [editingState, options]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingState({
      editingTextId: null,
      textContent: '',
      textStyle: { ...defaultTextStyle, ...options.defaultStyle },
      cursorPosition: { x: 0, y: 0 },
    });
  }, [options.defaultStyle]);

  // Update text content
  const updateTextContent = useCallback((content: string) => {
    setEditingState(prev => ({ ...prev, textContent: content }));
  }, []);

  // Update text style
  const updateTextStyle = useCallback((styleUpdates: Partial<TextStyle>) => {
    setEditingState(prev => ({
      ...prev,
      textStyle: { ...prev.textStyle, ...styleUpdates },
    }));
  }, []);

  // Toggle bold
  const toggleBold = useCallback(() => {
    setEditingState(prev => {
      const currentStyle = prev.textStyle.fontStyle;
      let newStyle: TextStyle['fontStyle'] = 'normal';
      
      if (currentStyle === 'normal') {
        newStyle = 'bold';
      } else if (currentStyle === 'italic') {
        newStyle = 'bold italic';
      } else if (currentStyle === 'bold') {
        newStyle = 'normal';
      } else if (currentStyle === 'bold italic') {
        newStyle = 'italic';
      }
      
      return {
        ...prev,
        textStyle: { ...prev.textStyle, fontStyle: newStyle },
      };
    });
  }, []);

  // Toggle italic
  const toggleItalic = useCallback(() => {
    setEditingState(prev => {
      const currentStyle = prev.textStyle.fontStyle;
      let newStyle: TextStyle['fontStyle'] = 'normal';
      
      if (currentStyle === 'normal') {
        newStyle = 'italic';
      } else if (currentStyle === 'bold') {
        newStyle = 'bold italic';
      } else if (currentStyle === 'italic') {
        newStyle = 'normal';
      } else if (currentStyle === 'bold italic') {
        newStyle = 'bold';
      }
      
      return {
        ...prev,
        textStyle: { ...prev.textStyle, fontStyle: newStyle },
      };
    });
  }, []);

  return {
    editingState,
    isEditing: editingState.editingTextId !== null,
    startEditing,
    saveText,
    cancelEditing,
    updateTextContent,
    updateTextStyle,
    toggleBold,
    toggleItalic,
  };
};
